import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { graphql, useMutation, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import UploadZone from "@app/pages/statements/upload-zone";
import PreflightChecklist from "@app/pages/data-health/preflight-checklist";
import SourceWindowsPanel from "@app/pages/data-health/source-windows-panel";
import type { WindowEdit } from "@app/pages/data-health/source-windows-panel";
import type { dataHealthPage_Query } from "@app/queries/dataHealthPage_Query.graphql";
import type { dataHealthPage_ConfigBackupMutation } from "@app/queries/dataHealthPage_ConfigBackupMutation.graphql";
import type { dataHealthPage_HistoryStartMutation } from "@app/queries/dataHealthPage_HistoryStartMutation.graphql";
import type { dataHealthPage_SourceWindowMutation } from "@app/queries/dataHealthPage_SourceWindowMutation.graphql";

const configBackupMutation = graphql`
  mutation dataHealthPage_ConfigBackupMutation {
    configBackup {
      configBackup {
        id
        filename
      }
      errors {
        message
      }
    }
  }
`;

// Backs up everything restorable — counterparties, rules, attributions,
// decisions, perspectives + categorizations. One click stores a timestamped
// snapshot in object storage, where it outlives the server it was taken on;
// /data-health/backups lists them and hands them back.
function ConfigBackupButton() {
  const [commit, inFlight] =
    useMutation<dataHealthPage_ConfigBackupMutation>(configBackupMutation);
  const [notice, setNotice] = useState<string | null>(null);

  const backup = () =>
    commit({
      variables: {},
      onCompleted: (response) => {
        const result = response.configBackup;
        if (!result?.configBackup) {
          setNotice(result?.errors[0]?.message ?? "Backup failed.");
          return;
        }
        setNotice(`Saved as ${result.configBackup.filename}.`);
      },
      onError: (error) => setNotice(error.message),
    });

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-3">
        <Link
          to="/data-health/backups"
          className="rounded border border-gray-200 text-gray-600 hover:bg-gray-50 px-3 py-1.5 text-sm"
        >
          All backups
        </Link>
        <button
          type="button"
          disabled={inFlight}
          onClick={backup}
          className="rounded bg-gray-900 text-white px-3 py-1.5 text-sm font-medium disabled:opacity-50"
          title="Store a timestamped snapshot of the full config in object storage"
        >
          {inFlight ? "Backing up…" : "Back up everything"}
        </button>
      </div>
      {/* Its own line, wrapped, so a message longer than a few words stays
          readable next to the buttons. */}
      {notice && (
        <span className="max-w-md text-right text-xs text-gray-500">
          {notice}
        </span>
      )}
    </div>
  );
}

const historyStartMutation = graphql`
  mutation dataHealthPage_HistoryStartMutation($historyStartOn: ISO8601Date) {
    accountHistoryStartSet(historyStartOn: $historyStartOn) {
      historyStartOn
      errors {
        message
      }
    }
  }
`;

const sourceWindowMutation = graphql`
  mutation dataHealthPage_SourceWindowMutation(
    $sourceKey: String!
    $declaredFrom: ISO8601Date
    $declaredTo: ISO8601Date
    $reason: String
  ) {
    sourceWindowSet(
      sourceKey: $sourceKey
      declaredFrom: $declaredFrom
      declaredTo: $declaredTo
      reason: $reason
    ) {
      sourceWindow {
        key
      }
      errors {
        message
      }
    }
  }
`;

export const dataHealthQuery = graphql`
  query dataHealthPage_Query {
    dataHealth {
      historyStartOn
      preflight {
        score {
          green
          amber
          red
          total
        }
        groups {
          code
          title
          greenCount
          totalCount
          checks {
            key
            title
            status
            detail
            actual
            target
            actionPath
            actionLabel
          }
        }
      }
      sourceWindows {
        key
        kind
        label
        dataFrom
        dataTo
        declaredFrom
        declaredTo
        effectiveFrom
        effectiveTo
        reason
        lateStart
        explained
        outOfWindowCount
        outOfWindowSample {
          date
          description
          amount
        }
      }
      gapMonths
      accounts {
        number
        kind
        firstMonth
        lastMonth
        coveredMonths
        gapMonths
        staleSince
        driftMonths {
          month
          value
        }
      }
      duplicateCharges {
        date
        amount
        description
        accountNumbers
        count
      }
    }
  }
`;

function monthRange(from: string, to: string): string[] {
  const months: string[] = [];
  let [year, month] = from.split("-").map(Number);
  const [toYear, toMonth] = to.split("-").map(Number);

  // Safety cap: this view audits raw imported data and renders one column per
  // month, so a single bad/implausible date (e.g. a misparsed year) must not be
  // able to blow the grid up to thousands of columns and hang the browser.
  // 600 months (50 years) is far beyond any real account history.
  while (
    (year < toYear || (year === toYear && month <= toMonth)) &&
    months.length < 600
  ) {
    months.push(`${year}-${String(month).padStart(2, "0")}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

// The account floor. Without it every source is measured only against itself,
// so an account that imported one year has no gaps inside that year and the
// whole checklist reads green — which is exactly the false comfort this page
// exists to remove.
function HistoryStartControl({
  value,
  onSaved,
}: {
  value: string | null | undefined;
  onSaved: () => void;
}) {
  const [commit, inFlight] =
    useMutation<dataHealthPage_HistoryStartMutation>(historyStartMutation);
  const [draft, setDraft] = useState(value ?? "");

  const save = (next: string) =>
    commit({
      variables: { historyStartOn: next === "" ? null : next },
      onCompleted: onSaved,
    });

  return (
    <div
      className={`rounded border p-3 ${
        value ? "bg-white" : "border-amber-300 bg-amber-50"
      }`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-700">
          This business&rsquo;s financial history begins
        </label>
        <input
          type="date"
          value={draft}
          disabled={inFlight}
          onChange={(event) => {
            setDraft(event.target.value);
            save(event.target.value);
          }}
          className="rounded border px-2 py-1 text-sm"
        />
        {inFlight && <span className="text-xs text-gray-400">Saving…</span>}
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {value
          ? "Every source is measured against this date — one that starts later is flagged until you import the missing history or say why it starts late."
          : "Until this is set, each source is only checked against itself: a source that was never imported for earlier years looks complete."}
      </p>
    </div>
  );
}

export default function DataHealthPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<dataHealthPage_Query>;
}) {
  const data = usePreloadedQuery(dataHealthQuery, queryRef);
  const accounts = data.dataHealth.accounts;
  const health = data.dataHealth;

  const [commitWindow] =
    useMutation<dataHealthPage_SourceWindowMutation>(sourceWindowMutation);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  // Both gestures change what the checklist concludes, not just the row edited —
  // a floor moving re-decides every source's lateness — so the whole page is
  // re-read rather than patched in the store.
  const reload = () => window.location.reload();

  const saveWindow = (sourceKey: string, edit: WindowEdit) => {
    setSavingKey(sourceKey);
    commitWindow({
      variables: {
        sourceKey,
        declaredFrom: edit.declaredFrom,
        declaredTo: edit.declaredTo,
        reason: edit.reason,
      },
      onCompleted: reload,
      onError: () => setSavingKey(null),
    });
  };

  const checklist = (
    <>
      <HistoryStartControl value={health.historyStartOn} onSaved={reload} />
      <PreflightChecklist
        groups={health.preflight.groups}
        score={health.preflight.score}
      />
      <SourceWindowsPanel
        sources={health.sourceWindows}
        historyStartOn={health.historyStartOn}
        onSaveWindow={saveWindow}
        savingKey={savingKey}
      />
    </>
  );

  if (accounts.length === 0) {
    return (
      <div className="p-6 max-w-full mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-bold">Data health</h1>
          <ConfigBackupButton />
        </div>
        <UploadZone />
        {checklist}
        <p className="text-gray-500">No statement data imported yet.</p>
      </div>
    );
  }

  // The grid runs from the earliest transaction month to the current month, so
  // accounts that haven't imported recent statements show their missing tail.
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, "0")}`;
  const globalFirst = accounts.map((a) => a.firstMonth).sort()[0];
  const dataLast = accounts
    .map((a) => a.lastMonth)
    .sort()
    .at(-1)!;
  const globalLast = [dataLast, currentMonth].sort().at(-1)!;
  const allMonths = monthRange(globalFirst, globalLast);
  const duplicates = data.dataHealth.duplicateCharges;

  return (
    <div className="p-6 max-w-full mx-auto space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold">Data health</h1>
        <ConfigBackupButton />
      </div>
      <UploadZone />
      {checklist}

      <h2 className="pt-2 text-sm font-medium text-gray-700">
        Statement coverage
      </h2>
      <p className="text-sm text-gray-500">
        Each cell is one account-month.{" "}
        <span className="inline-block w-3 h-3 bg-green-200 rounded-sm align-middle" />{" "}
        covered ·{" "}
        <span className="inline-block w-3 h-3 bg-red-400 rounded-sm align-middle" />{" "}
        gap (no statement file covers it) ·{" "}
        <span className="inline-block w-3 h-3 bg-amber-400 rounded-sm align-middle" />{" "}
        balance drift (statement balance ≠ imported transactions) ·{" "}
        <span className="inline-block w-3 h-3 bg-gray-100 border rounded-sm align-middle" />{" "}
        outside the account's coverage span
      </p>

      <div className="overflow-x-auto bg-white rounded border p-4">
        <table className="text-xs border-separate" style={{ borderSpacing: 2 }}>
          <thead>
            <tr>
              <th className="text-left pr-3 sticky left-0 bg-white">Account</th>
              {allMonths.map((month) => (
                <th
                  key={month}
                  className="font-normal text-gray-400"
                  style={{ writingMode: "vertical-rl", fontSize: 9 }}
                >
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => {
              const covered = new Set(account.coveredMonths);
              const gaps = new Set(account.gapMonths);
              const drifts = new Map(
                account.driftMonths.map((d) => [d.month, d.value])
              );

              return (
                <tr key={account.number}>
                  <td className="pr-3 whitespace-nowrap sticky left-0 bg-white">
                    <span className="font-medium">{account.number}</span>
                    <span className="text-gray-400 ml-1">
                      {account.kind.toLowerCase()}
                    </span>
                    {account.staleSince && (
                      <span className="ml-1 text-red-600 font-medium">
                        stale since {account.staleSince}
                      </span>
                    )}
                  </td>
                  {allMonths.map((month) => {
                    const drift = drifts.get(month);
                    const cellClass = gaps.has(month)
                      ? "bg-red-400"
                      : drift != null
                      ? "bg-amber-400"
                      : covered.has(month)
                      ? "bg-green-200"
                      : "bg-gray-100";
                    const title = gaps.has(month)
                      ? `${account.number} ${month}: NO statement coverage`
                      : drift != null
                      ? `${
                          account.number
                        } ${month}: balance drift $${drift.toLocaleString()}`
                      : `${account.number} ${month}`;

                    return (
                      <td
                        key={month}
                        title={title}
                        className={`${cellClass} rounded-sm`}
                        style={{ width: 12, height: 16 }}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {duplicates.length > 0 && (
        <div className="bg-white rounded border p-4">
          <h2 className="font-bold text-amber-700">
            ⚠ Same charge on multiple accounts
          </h2>
          <p className="text-sm text-gray-500 mb-2">
            Identical external charges on more than one account — usually the
            same statement imported under two account numbers. Review whether
            one account is a duplicate.
          </p>
          <ul className="text-sm divide-y">
            {duplicates.map((d, i) => (
              <li key={i} className="py-1.5 flex justify-between gap-4">
                <span className="text-gray-900">
                  {d.date} · {d.description}
                </span>
                <span className="text-gray-600 tabular-nums">
                  {d.amount.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                  {" — on accounts "}
                  {[...d.accountNumbers].join(", ")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-sm text-gray-600 space-y-1">
        {accounts
          .filter((a) => a.gapMonths.length > 0)
          .map((a) => (
            <p key={a.number}>
              <span className="font-medium">{a.number}</span> is missing{" "}
              {a.gapMonths.length} month
              {a.gapMonths.length > 1 ? "s" : ""}: {a.gapMonths.join(", ")} —
              re-download these from the bank and upload them.
            </p>
          ))}
      </div>
    </div>
  );
}
